import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { numberStep, stringStep } from '../../../concepts/commands/CommandWizard';
import { Component, ObjectManager } from '../../../concepts/objects';
import { toRadians } from '../../../platform/geom/Math';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { PopupManager } from '../../popups/PopupManager';
import { AdapterInfo } from '../AdapterInfo';
import { EditorSession } from '../EditorSession';
import { CreateDuplicateInfo, DuplicateInfo } from '../entities/DuplicateInfo';
import { Physical, RelativeTo, TransformScope } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Resizable } from '../entities/Resizable';
import { Root } from '../entities/Root';
import { ImagePopup } from '../properties/ImagePopup';
import { PropertyStripInfo } from '../properties/PropertyStripInfo';
import { RootOverride } from '../RootOverride';
import { Selection, SelectionSet } from '../Selection';
import { toolEquip } from '../tooling/ToolCommands';
import { ToolInfo } from '../tooling/ToolInfo';
import { resolveSubject, resolveSubjects } from './CommandUtil';
import { place } from './OrderCommands';
import { clearSelection, select } from './SelectionCommands';

export const widthFitParent = defineCommand(
    'widthFitParent',
    'Width: Fit Parent',
    (r, [targetIds]: [string[]?]) => {

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        objects.update(() => {
            for (const x of targets) {
                const t = x.as(Resizable);
                const p = t?.parent?.as(Physical);
                if (!t || !p) continue;
                t.moveTo({ x: 0 });
                t.resize(p.size().w, t.height);
            }
        });

        session.apply(Selection, s => s.replace(targets));
    },
);

export const heightFitParent = defineCommand(
    'heightFitParent',
    'Height: Fit Parent',
    (r, [targetIds]: [string[]?]) => {

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        objects.update(() => {
            for (const x of targets) {
                const t = x.as(Resizable);
                const p = t?.parent?.as(Physical);
                if (!t || !p) continue;
                t.moveTo({ y: 0 });
                t.resize(t.width, p.size().h);
            }
        });

        session.apply(Selection, s => s.replace(targets));
    },
);

export const sizeFitParent = defineCommand(
    'sizeFitParent',
    'Size: Fit Parent',
    (r, [targetIds]: [string[]?]) => {
        const dispatch = r.call(CommandDispatcher);
        dispatch(widthFitParent, [targetIds]);
        dispatch(heightFitParent, [targetIds]);
    }
);

export const sizeShrink = defineCommand(
    'sizeShrink',
    'Size: Shrink',
    (r, [amount, targetIds]: [number, string[]?]) => {

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        amount = amount || 10;

        objects.update(() => {
            for (const x of targets) {
                const t = x.as(Resizable);
                const p = t?.parent?.as(Physical);
                if (!t || !p) continue;
                t.moveBy(amount / 2, amount / 2);
                t.resize(t.width - amount, t.height - amount);
            }
        });

        session.apply(Selection, s => s.replace(targets));
    },
    [
        numberStep('amount', 'Shrink by how much?')
    ]
);

export const rotateReset = defineCommand(
    'rotateReset',
    'Rotate: Reset',
    (r, [targetIds]: [string[]?]) => {
        r.call(CommandDispatcher)(rotateTo, [0, targetIds]);
    },
);

export const rotateTo = defineCommand(
    'rotateTo',
    'Rotate: To...',
    (r, [rotation, targetIds]: [number, string[]?]) => {

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        const radians = toRadians(rotation);
        const origins = Object.fromEntries(targets.map(x => [x.id, x.center(RelativeTo.Model)]));
        const scope = new TransformScope(targets, 'rotate');

        objects.update(() => {
            for (const t of targets) {
                const mt = Matrix.identity
                    .translate(origins[t.id])
                    .rotate(radians - t.rotation(RelativeTo.Parent))
                    .translate(origins[t.id].multiply(-1));
                const from = t.createFigure();
                const to = mt.apply(from);
                t.applyTransformation(scope, from, to);
            }
        });

        session.apply(Selection, s => s.replace(targets));
    },
    [
        numberStep('degrees', 'Enter a rotation in degrees:')
    ]
);

export const setName = defineCommand(
    'setName',
    'Name: Set...',
    (r, [value, targetIds]: [string, string[]?]) => {

        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        objects.update(() => {
            for (const t of targets) {
                t.name = value || null;
            }
        });
    },
    [
        stringStep('name', 'Enter a name:')
    ]
);

export const set = defineCommand(
    'set',
    'Set...',
    (r, [property, value, targetIds]: [string, string, string[]?]) => {

        const objects = r.call(ObjectManager);

        const targets = resolveSubjects(r, targetIds);
        if (!targets.length) return;

        objects.update(() => {
            for (const t of targets) {
                const desc = Component.describe(t);
                const prop = desc.properties.find(x => x.name === property);
                if (!prop || prop.readonly) continue;
                const [result, safeValue] = prop.type.fromString(value);
                if (!result) continue;
                t.set(property, safeValue);
            }
        });
    },
    [
        stringStep('property', 'Enter the property to set:'),
        stringStep('value', 'Enter the value to set:'),
    ]
);

export const beginMutating = defineCommand(
    'beginMutating',
    'Edit',
    (r, [targetId, shouldSelect]: [string?, boolean?]) => {

        const dispatch = r.call(CommandDispatcher);
        const session = r.call(EditorSession);

        const subject = resolveSubject(r, targetId);
        if (!subject) return;

        const desc = Component.describe(subject);
        if (!desc.mutation) return;

        if (shouldSelect) {
            session.update(Selection, SelectionSet.create([subject]));
        }
        dispatch(desc.mutation, [subject.id]);
    },
    [
        stringStep('targetId', 'Target ID (leave blank for selected):'),
    ]
);

export const duplicate = defineCommand(
    'duplicate',
    null,
    (r, [mode, targets]: [('default' | 'tab' | 'newline')?, string[]?]) => {
        mode = mode ?? 'default';

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const subjects = resolveSubjects(r, targets);
        if (!subjects.length) return;

        const prevInfo = session.get(DuplicateInfo) ?? DuplicateInfo.empty;
        const root = session.get(RootOverride) ?? objects.get('root', Root);

        objects.update(u => {

            // a = previous subject (possible null)
            // b = subject
            // c = clone

            const bb = Physicals.bounds(subjects);

            const mappings = Object.fromEntries(subjects.map(b => {

                const a = prevInfo.mode === mode
                    ? objects.getMaybe(prevInfo.mappings[b.id] ?? '', Physical)
                    : null;
                const p = mode === 'default'
                    ? u.activate(b.toTuple()).cast(Physical)
                    : u.activate(b.toEmptyTuple()).cast(Physical);

                root.add(p);

                if (!!a) {
                    const ap = a.position(RelativeTo.Model);
                    const bp = b.position(RelativeTo.Model);
                    p.moveTo(bp.add(bp.subtract(ap)));
                }
                else {
                    const dv = Point[mode === 'newline' ? 'down' : 'right'];
                    const bp = b.position(RelativeTo.Model);
                    const cv = dv.multiply(bb.size()).add(dv.multiply(10));
                    p.moveTo(bp.x, bp.y);
                    p.moveBy(cv.x, cv.y);
                }

                return [p.id, b.id];
            }));

            const cloneIds = Object.keys(mappings);
            dispatch(place, [cloneIds]);
            dispatch(select, [cloneIds]);
            session.update(DuplicateInfo, CreateDuplicateInfo(mode, mappings));
        });

    }
);

export const togglePropertyStripMode = defineCommand(
    'togglePropertyStripMode',
    null,
    r => {

        const dispatch = r.call(CommandDispatcher);
        const session = r.call(EditorSession);

        const selection = session.get(Selection);
        const { mode: prev } = session.get(PropertyStripInfo);
        const next = prev === 'auto' ? 'field' : 'auto';

        session.patch(PropertyStripInfo, { mode: next });

        if (selection.size === 1) {
            if (next === 'field') {
                dispatch(beginMutating);
            }
            else {
                session.update(AdapterInfo, AdapterInfo.empty);
            }
        }
    }
);

export const popup = defineCommand(
    'popup',
    null,
    (r) => {
        const popups = r.call(PopupManager);
        popups.invoke({ ui: ImagePopup });
    }
);

export const escape = defineCommand(
    'escape',
    'Escape',
    (r) => {
        // Exit current tool, unless tool is default in which case clear selection.
        const dispatch = r.call(CommandDispatcher);
        const session = r.call(EditorSession);
        const adapter = session.get(AdapterInfo);
        const tool = session.get(ToolInfo);
        if (adapter.target !== 'none') {
            session.update(AdapterInfo, AdapterInfo.empty);
        }
        if (tool.id !== 'default') {
            dispatch(toolEquip, ['default']);
        }
        else {
            dispatch(clearSelection);
        }
    }
);
